import { useState, useEffect, useMemo, memo } from "react";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import TopTwoColLayout from "../components/homePartials/TopTwoColLayout";
import PromoSlider from "../components/homePartials/PromoSlider";
import OneColLayout from "../components/homePartials/OneColLayout";
import TopThreeColLayout from "../components/homePartials/TopThreeColLayout";
import Footer from "../components/common/Footer";
import MealSwiper from "../components/homePartials/MealSwiper";
import PlanSlider from "../components/homePartials/PlanSlider";
import SpotLight from "../components/homePartials/SpotLight";
import MainLoading from "../components/common/loading/MainLoading";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";
import { useGeneral } from "../components/contexts/GeneralContext";
import { useCart } from "../components/contexts/CartContext";
import { useAuth } from "../components/contexts/AuthContext";

/* ------------------ Custom Hooks ------------------ */
const useHomePageContent = () => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    banners: [],
    meals: [],
    spotLights: { left: [], center: [], right: [] },
    plans: [],
    discountCoupons: [],
    specialDiscount: [],
  });
  const { discountCoupons, setDiscountCoupons, fetchExtraContent } =
    useGeneral(); // added fetchExtraContent
  const { fetchCart } = useCart();
  const { isloggedIn } = useAuth();
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await axios.get("get-home-page-content");

        if (active) {
          const allCoupons = [
            ...(data?.discountCoupons || []),
            ...(data?.specialDiscountCoupons || []),
          ];

          setDiscountCoupons(allCoupons);
          localStorage.setItem("discountCoupons", JSON.stringify(allCoupons));

          setContent(data);
        }
      } catch {
        if (active) setLoading(false);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [fetchExtraContent, setDiscountCoupons]);

  return { content, loading, discountCoupons };
};

const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

/* ------------------ Static Default Content ------------------ */
const homePageDefaults = Object.freeze({
  mealSlider: [
    {
      shortHeading: "Explore our",
      mainHeading: "Meals",
      title: "Breakfast",
      slideBackgroundColor: "#355e25",
      slideSectionBackgroundColor: "#500",
      slideSectionTextColor: "#fff",
      headingColor: "#fff",
      sortOrder: 4,
      status: "1",
      slideImage: "meals-Lunch-UTSdFzAPK4.png",
    },
    {
      shortHeading: "Explore our",
      mainHeading: "Meals",
      title: "Lunch",
      slideBackgroundColor: "#30160E",
      slideSectionBackgroundColor: "#fff",
      slideSectionTextColor: "#000",
      headingColor: "#fff",
      slideImage: "meals-Lunch-UTSdFzAPK4.png",
      sortOrder: 2,
      status: "1",
    },
    {
      shortHeading: "Explore our",
      mainHeading: "Meals",
      title: "Snack",
      slideBackgroundColor: "#D06F36",
      slideSectionBackgroundColor: "#500",
      slideSectionTextColor: "#fff",
      headingColor: "#fff",
      slideImage: "meals-Lunch-UTSdFzAPK4.png",
      sortOrder: 1,
      status: "1",
    },
    {
      shortHeading: "Explore our",
      mainHeading: "Meals",
      title: "Dinner",
      slideBackgroundColor: "#CC9965",
      slideSectionBackgroundColor: "#500",
      slideSectionTextColor: "#fff",
      headingColor: "#fff",
      slideImage: "meals-Lunch-UTSdFzAPK4.png",
      sortOrder: 3,
      status: "1",
    },
  ],
  spotLights: {
    left: [
      {
        slideBackgroundColor: "#A0AE3F",
        heading1: "Healthy",
        heading2: "Recipes",
        imagePosition: "Right",
        heading1Color: "#fff",
        heading2Color: "#fff",
        image: "meals-Lunch-UTSdFzAPK4.png",
        buttonBackgroundColor: "#500",
        buttonTextColor: "#ccc",
        buttonLink: "/recipes",
        buttonText: "Lets cook",
        buttonIcon: "+",
        sortOrder: 1,
      },
    ],
    center: [
      {
        slideBackgroundColor: "#CC9965",
        heading1: "NutriCub",
        heading2: "Shop",
        imagePosition: "Left",
        heading1Color: "#fff",
        heading2Color: "#fff",
        image: "meals-Lunch-UTSdFzAPK4.png",
        buttonBackgroundColor: "#A0AE3F",
        buttonTextColor: "#500",
        buttonLink: "/shop",
        buttonText: "All Products",
        buttonIcon: "+",
        sortOrder: 1,
      },
    ],
    right: [
      {
        slideBackgroundColor: "#30160E",
        heading1: "NutiCub",
        heading2: "Standards",
        imagePosition: "Right",
        heading1Color: "#fff",
        heading2Color: "#fff",
        buttonBackgroundColor: "rgba(218, 103, 83, 1)",
        buttonTextColor: "#ccc",
        image: "meals-Lunch-UTSdFzAPK4.png",
        buttonLink: "/about?active=why",
        buttonText: "why us?",
        buttonIcon: "",
        sortOrder: 2,
      },
    ],
  },
});

/* ------------------ Memoized Child Wrapper ------------------ */
const MemoTopLayouts = memo(({ content }) => {
  const mealSlides =
    content.meals.length > 0 ? content.meals : homePageDefaults.mealSlider;

  const leftSpot = content.spotLights?.left?.length
    ? content.spotLights.left
    : homePageDefaults.spotLights.left;
  const centerSpot = content.spotLights?.center?.length
    ? content.spotLights.center
    : homePageDefaults.spotLights.center;
  const rightSpot = content.spotLights?.right?.length
    ? content.spotLights.right
    : homePageDefaults.spotLights.right;
  const homePageProducts = content.spotLights?.homePageProducts?.length
    ? content.spotLights.homePageProducts
    : [];
  return (
    <div className="flex flex-col md:flex-col gap-2 md:gap-4 lg:gap-10">
      <div className="order-1">
        <TopTwoColLayout
          leftContent={<PromoSlider slides={content.banners} />}
          rightContent={<MealSwiper slides={mealSlides} />}
        />
      </div>
      {content.plans.length > 0 && (
        <div className="order-2 md:order-4 xl:order-4 lg:order-4">
          <OneColLayout content={<PlanSlider slides={content.plans} />} />
        </div>
      )}

      <div className="order-3 xl:order-2 lg:order-2 md:order-2">

        <TopThreeColLayout
          leftContent={<SpotLight slides={leftSpot} position="Left" />}
          middleContent={
            <SpotLight
              slides={centerSpot}
              products={homePageProducts}
              position="Center"
            />
          }
          rightContent={<SpotLight slides={rightSpot} position="Right" />}
        />
      </div>
    </div>
  );
});
MemoTopLayouts.displayName = "MemoTopLayouts";

/* ------------------ Main Component ------------------ */
const HomePage = () => {
  const { content, loading, discountCoupons } = useHomePageContent();
  useScrollToTop();

  const memoizedContent = useMemo(() => content, [content, discountCoupons]);

  return (
    <>
      <MainLoading />
      {loading && <FullScreenLoading />}
      <PageWrapper loading={loading}>
        <Header code={discountCoupons} />
        <BodyWrapper>
          {!loading && <MemoTopLayouts content={memoizedContent} />}
        </BodyWrapper>
        <Footer />
      </PageWrapper>
    </>
  );
};

export default HomePage;
